import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { api } from './api';
import type { IconName } from './components/Icon';
import { useSession } from './session';

/**
 * Where everything lives, said once.
 *
 * The sidebar and the page headers both read from this, so a section cannot be called "Access" in the
 * menu and "Grants" on the page it opens. The path is relative to the workspace or org it belongs to; an
 * empty path is that workspace's or org's own page.
 */
export interface Section {
  path: string;
  label: string;
  icon: IconName;
  /** One line under the section heading, saying what this page is for. */
  blurb: string;
  /** Owners and admins only. Left out of the menu rather than shown disabled. */
  manage?: boolean;
}

export const WORKSPACE_SECTIONS: Section[] = [
  { path: '', label: 'Salesforce orgs', icon: 'cloud', blurb: 'The customer orgs this workspace connects to, production and sandbox.' },
  { path: 'members', label: 'People', icon: 'people', blurb: 'Who is in this workspace, and what they may do here.' },
  { path: 'grants', label: 'Access grants', icon: 'key', blurb: 'Who may open which org, as which Salesforce user, across every org here.', manage: true },
  { path: 'audit', label: 'Session log', icon: 'log', blurb: 'Every Salesforce session Kruvi Access has opened: who, into which org, and why.' },
  { path: 'skill-log', label: 'Skill log', icon: 'log', blurb: 'What agents were shown through skills: which records, which fields, what was redacted.' },
  { path: 'settings', label: 'Settings', icon: 'gear', blurb: 'How people in this workspace may reach Salesforce, and how long the logs are kept.', manage: true },
];

export const ORG_SECTIONS: Section[] = [
  { path: '', label: 'Salesforce users', icon: 'user', blurb: 'The Salesforce logins Kruvi Access may act as in this org.' },
  { path: 'grants', label: 'Access grants', icon: 'key', blurb: 'Who may use those logins, on which channels, and until when.', manage: true },
  { path: 'skills', label: 'Skills', icon: 'skill', blurb: 'What an agent may read and change here, written as a skill.', manage: true },
  { path: 'sessions', label: 'Session log', icon: 'log', blurb: 'Every session opened into this org.' },
  { path: 'skill-log', label: 'Skill log', icon: 'log', blurb: 'What agents were shown from this org through skills.' },
  { path: 'settings', label: 'Settings', icon: 'gear', blurb: 'Its name, what it may be used for, and taking it out of service.', manage: true },
];

/** Which section of a workspace or org a pathname is on, by its first segment after the base. */
export function sectionFor(sections: Section[], base: string, pathname: string): Section | undefined {
  const rest = pathname.startsWith(base) ? pathname.slice(base.length).replace(/^\/+/, '') : '';
  const first = rest.split('/')[0] ?? '';
  return sections.find((s) => s.path === first);
}

export const workspacePath = (tenantId: string, section = '') =>
  `/workspaces/${tenantId}${section ? `/${section}` : ''}`;
export const orgPath = (tenantId: string, orgId: string, section = '') =>
  `/workspaces/${tenantId}/orgs/${orgId}${section ? `/${section}` : ''}`;

export interface NavOrg {
  id: string;
  label: string;
  environment: string;
  status: string;
}

interface NavState {
  /** The workspace the menu is showing: the one in the address, else the last one visited. */
  tenantId: string | null;
  /** The org in the address, if there is one. */
  orgId: string | null;
  orgs: NavOrg[] | null;
  /** Call after anything that renames, connects, disables or removes an org, so the menu keeps up. */
  refreshOrgs: () => Promise<void>;
}

const NavContext = createContext<NavState | null>(null);

const LAST_WORKSPACE = 'kruvi:last-workspace';

function remembered(): string | null {
  try {
    return localStorage.getItem(LAST_WORKSPACE);
  } catch {
    return null;
  }
}

function remember(tenantId: string) {
  try {
    localStorage.setItem(LAST_WORKSPACE, tenantId);
  } catch {
    /* a private window: the menu just falls back to the first workspace */
  }
}

/** Reads the workspace and org out of the address. "new" is the connect page, not an org. */
export function useRouteIds(): { tenantId: string | null; orgId: string | null } {
  const { pathname } = useLocation();
  const org = matchPath('/workspaces/:tenantId/orgs/:orgId/*', pathname);
  const ws = matchPath('/workspaces/:tenantId/*', pathname);
  const orgId = org?.params.orgId && org.params.orgId !== 'new' ? org.params.orgId : null;
  return { tenantId: ws?.params.tenantId ?? null, orgId };
}

export function NavProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const route = useRouteIds();
  const memberships = me?.memberships ?? [];
  const known = (id: string | null) => (id && memberships.some((m) => m.tenantId === id) ? id : null);
  const tenantId = known(route.tenantId) ?? known(remembered()) ?? memberships[0]?.tenantId ?? null;
  const [orgs, setOrgs] = useState<NavOrg[] | null>(null);

  useEffect(() => {
    if (route.tenantId && route.tenantId === tenantId) remember(route.tenantId);
  }, [route.tenantId, tenantId]);

  // Only the latest request may write. Switching workspace while the last one's list is still on its way
  // would otherwise let it land late and fill the menu with another workspace's orgs.
  const latest = useRef(0);
  const refreshOrgs = useCallback(async () => {
    const mine = ++latest.current;
    if (!tenantId || !me?.mfaVerified) {
      setOrgs(null);
      return;
    }
    try {
      const r = await api<{ orgs: NavOrg[] }>(`/api/tenants/${tenantId}/orgs`);
      if (latest.current === mine) setOrgs(r.orgs);
    } catch {
      // The menu is a convenience; the page itself reports anything that matters.
      if (latest.current === mine) setOrgs([]);
    }
  }, [tenantId, me?.mfaVerified]);

  useEffect(() => {
    setOrgs(null);
    void refreshOrgs();
  }, [refreshOrgs]);

  return (
    <NavContext.Provider value={{ tenantId, orgId: route.orgId, orgs, refreshOrgs }}>{children}</NavContext.Provider>
  );
}

export function useNav(): NavState {
  const ctx = useContext(NavContext);
  if (!ctx) throw new Error('useNav outside NavProvider');
  return ctx;
}
